import React from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import { X, Loader2 } from 'lucide-react';
import { useUpdateProspectMutation } from '../../store/prospectApi';
import { toast } from '@clareefai/ui';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '+1000'] as const;
const PROSPECT_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'NEGOTIATION', 'INTRODUCE', 'WON', 'LOST'] as const;

const editProspectSchema = v.object({
  firstName: v.pipe(v.string(), v.minLength(2, 'Min 2 characters'), v.maxLength(50, 'Max 50 characters')),
  lastName: v.pipe(v.string(), v.minLength(2, 'Min 2 characters'), v.maxLength(50, 'Max 50 characters')),
  jobTitle: v.optional(v.union([v.pipe(v.string(), v.maxLength(100, 'Max 100 characters')), v.literal('')])),
  phone: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  company: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  companySize: v.optional(v.union([v.picklist(COMPANY_SIZES), v.literal('')])),
  sectors: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  notes: v.optional(v.union([v.pipe(v.string(), v.minLength(1)), v.literal('')])),
  status: v.picklist(PROSPECT_STATUSES),
});

type EditProspectFormData = v.InferOutput<typeof editProspectSchema>;

interface EditProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospect: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    jobTitle?: string | null;
    phone?: string | null;
    company?: string | null;
    companySize?: string | null;
    sectors?: string[];
    notes?: string | null;
    source?: string;
    status: string;
  };
}

const EditProspectModal: React.FC<EditProspectModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prospect,
}) => {
  const { t } = useTranslation();
  const [updateProspect, { isLoading }] = useUpdateProspectMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<EditProspectFormData>({
    resolver: valibotResolver(editProspectSchema),
    defaultValues: {
      firstName: prospect.firstName,
      lastName: prospect.lastName,
      jobTitle: prospect.jobTitle || '',
      phone: prospect.phone || '',
      company: prospect.company || '',
      companySize: (prospect.companySize as EditProspectFormData['companySize']) || '',
      sectors: prospect.sectors?.join(', ') || '',
      notes: prospect.notes || '',
      status: prospect.status as EditProspectFormData['status'],
    },
  });

  const onSubmit = async (data: EditProspectFormData) => {
    try {
      const body: Record<string, any> = {
        first_name: data.firstName,
        last_name: data.lastName,
        status: data.status,
      };
      if (data.jobTitle !== undefined) body.job_title = data.jobTitle || null;
      if (data.phone !== undefined) body.phone = data.phone || null;
      if (data.company !== undefined) body.company = data.company || null;
      if (data.companySize !== undefined) body.company_size = data.companySize || null;
      if (data.sectors !== undefined) body.sectors = data.sectors ? data.sectors.split(',').map(s => s.trim()).filter(Boolean) : [];
      if (data.notes !== undefined) body.notes = data.notes || null;

      await updateProspect({ id: prospect.id, data: body }).unwrap();
      toast.success(t('prospects.edit.updatedSuccess'));
      onSuccess();
    } catch (err: any) {
      toast.error(err?.data?.message || t('prospects.edit.updateFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={isLoading ? undefined : onClose} />
      <div className="relative z-50 w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{t('prospects.edit.title')}</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
            <input
              type="email"
              value={prospect.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">{t('prospects.edit.emailCannotChange')}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.firstName')}</label>
              <input
                {...register('firstName')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.firstName && (
                <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.lastName')}</label>
              <input
                {...register('lastName')}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.lastName && (
                <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.jobTitle')}</label>
            <input
              {...register('jobTitle')}
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                errors.jobTitle ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('prospects.edit.optional')}
            />
            {errors.jobTitle && (
              <p className="text-xs text-red-600 mt-1">{errors.jobTitle.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.phone')}</label>
            <input
              {...register('phone')}
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
              placeholder={t('prospects.edit.optional')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.company')}</label>
              <input
                {...register('company')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
                placeholder={t('prospects.edit.optional')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('prospects.companySize')}</label>
              <select
                {...register('companySize')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
              >
                <option value="">{t('prospects.edit.selectSize')}</option>
                {COMPANY_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('prospects.edit.sectors')}</label>
            <input
              {...register('sectors')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0]"
              placeholder={t('prospects.edit.sectorsPlaceholder')}
            />
            <p className="text-xs text-gray-400 mt-1">{t('prospects.edit.sectorsHelp')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
            <select
              {...register('status')}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] ${
                errors.status ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              {PROSPECT_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            {errors.status && (
              <p className="text-xs text-red-600 mt-1">{errors.status.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4339A0] resize-none"
              placeholder={t('prospects.edit.optionalNotes')}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !isDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-[#4339A0] rounded-lg hover:bg-[#362d85] transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{t('common.saveChanges')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProspectModal;
